import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { TooltipContent, TooltipRoot, TooltipTrigger } from '@/components/ui/tooltip';

export interface AllocationCategoryData {
  key: string;
  label: string;
  value: number;
  percentage: number;
  count: number;
  color: string;
}

interface InvestmentAllocationChartProps {
  data: AllocationCategoryData[];
  totalAmount: number;
  className?: string;
  selectedCategories?: Set<string>;
  isCategoryNegated?: boolean;
  onCategoryClick?: (categoryKey: string | null) => void;
}

interface DonutSegment {
  entry: AllocationCategoryData;
  pct: number;
  offset: number;
}

function DonutSlice({
  segment,
  dimmed,
  title,
  onCategoryClick,
}: {
  segment: DonutSegment;
  dimmed: boolean;
  title: string;
  onCategoryClick?: (categoryKey: string | null) => void;
}) {
  const { entry, pct, offset } = segment;

  function handleClick() {
    onCategoryClick?.(entry.key);
  }

  // TooltipTrigger renders asChild directly onto the <circle> (a wrapper element
  // would be invalid inside <svg>), giving slices the same dark-glass tooltip as
  // the page's ECharts tooltips instead of the native browser title.
  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <circle
          cx="21"
          cy="21"
          r="15.9155"
          fill="none"
          stroke={entry.color}
          strokeWidth="6"
          pathLength={100}
          strokeDasharray={`${pct} ${100 - pct}`}
          strokeDashoffset={-offset}
          opacity={dimmed ? 0.3 : 1}
          className="cursor-pointer transition-opacity hover:opacity-80"
          onClick={handleClick}
        />
      </TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </TooltipRoot>
  );
}

function AllocationLegendRow({
  entry,
  isSelected,
  isCategoryNegated,
  onCategoryClick,
}: {
  entry: AllocationCategoryData;
  isSelected: boolean;
  isCategoryNegated: boolean;
  onCategoryClick?: (categoryKey: string | null) => void;
}) {
  const isExcluded = isSelected && isCategoryNegated;
  const isIncluded = isSelected && !isCategoryNegated;

  function handleClick() {
    onCategoryClick?.(entry.key);
  }

  return (
    <button
      onClick={handleClick}
      aria-pressed={isSelected}
      title={
        isSelected
          ? 'Clique para remover da seleção'
          : 'Clique para adicionar à seleção'
      }
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all ${
        isExcluded
          ? 'bg-error-surface ring-1 ring-error'
          : isIncluded
            ? 'bg-brand-50 ring-1 ring-border-brand'
            : 'hover:bg-surface-sunken bg-surface-sunken sm:bg-transparent'
      } ${onCategoryClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div
        className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isExcluded ? 'opacity-30' : ''}`}
        style={{ backgroundColor: entry.color }}
      />
      <div className="flex min-w-0 flex-1 flex-col items-start">
        <span className={`text-xs font-semibold truncate text-left ${
          isExcluded ? 'text-error-text line-through' : isIncluded ? 'text-brand-700' : 'text-text-secondary'
        }`}>
          {entry.label}
        </span>
        <span className="font-mono text-2xs tabular-nums text-text-tertiary">
          {entry.count} ativo{entry.count !== 1 ? 's' : ''}
        </span>
      </div>
      <span className={`font-mono text-xs font-bold tabular-nums flex-shrink-0 ${
        isExcluded ? 'text-error-text' : isIncluded ? 'text-brand-700' : 'text-text-primary'
      }`}>
        {entry.percentage.toFixed(1)}%
      </span>
    </button>
  );
}

export function InvestmentAllocationChart({
  data,
  totalAmount,
  className = '',
  selectedCategories = new Set(),
  isCategoryNegated = false,
  onCategoryClick,
}: InvestmentAllocationChartProps) {
  // Privacy-aware formatter — the donut <title> tooltips carry real amounts, so
  // they must go through PrivacyContext like every other money string.
  const formatCurrency = useFormatCurrency();
  const hasSelection = selectedCategories.size > 0;
  const hasData = data.length > 0 && totalAmount > 0;

  if (!hasData) {
    return (
      <div className={`flex flex-col items-center justify-center text-center p-4 ${className}`}>
        <p className="text-sm font-medium text-text-tertiary">Sem dados de alocação</p>
      </div>
    );
  }

  // Donut geometry (mockup-approved form): pathLength=100 makes stroke-dasharray
  // percentage-based, so segments are cumulative-offset slices of one circle.
  let cumulative = 0;
  const segments: DonutSegment[] = data
    .filter((entry) => totalAmount > 0 && entry.value > 0)
    .map((entry) => {
      const pct = (entry.value / totalAmount) * 100;
      const segment = { entry, pct, offset: cumulative };
      cumulative += pct;
      return segment;
    });

  return (
    <div className={`flex h-full flex-col ${className}`}>
      <div className="flex h-full flex-col items-stretch gap-4 sm:flex-row sm:items-center">
        {/* Donut — slices are clickable (pointer users); the legend rows beside it
            are the accessible buttons for the same filter action. */}
        <svg viewBox="0 0 42 42" className="mx-auto size-28 shrink-0 -rotate-90 sm:size-32" aria-hidden>
          {segments.map((segment) => {
            const isSelected = selectedCategories.has(segment.entry.key);
            const dimmed = hasSelection && (
              isCategoryNegated ? isSelected : !isSelected
            );
            return (
              <DonutSlice
                key={segment.entry.key}
                segment={segment}
                dimmed={dimmed}
                title={`${segment.entry.label}: ${formatCurrency(segment.entry.value)} (${segment.entry.percentage.toFixed(1)}%)`}
                onCategoryClick={onCategoryClick}
              />
            );
          })}
        </svg>

        <div className="w-full min-w-0 flex-1">
          <div className="space-y-1">
            {data.map((entry) => (
              <AllocationLegendRow
                key={entry.key}
                entry={entry}
                isSelected={selectedCategories.has(entry.key)}
                isCategoryNegated={isCategoryNegated}
                onCategoryClick={onCategoryClick}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
